'use client';

import { ReactNode, useEffect } from 'react';
import { useAuthStore } from '../hooks/auth/useAuthStore';
import { onAuthFailure } from '../api/http';

interface AuthProviderProps {
  children: ReactNode;
  /** Called when the session dies (refresh token expired/revoked). Apps redirect to sign-in. */
  onSignedOut?: () => void;
}

/**
 * Hydrates the user from a persisted token on mount and wires the api layer's
 * auth-failure signal to the app. No token polling — the HTTP client refreshes
 * lazily (15-min access, rotating refresh) on the request that needs it.
 */
export const AuthProvider = ({ children, onSignedOut }: AuthProviderProps) => {
  const hydrate = useAuthStore((s) => s.hydrate);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    hydrate();
    const unsubscribe = onAuthFailure(() => {
      logout();
      onSignedOut?.();
    });
    return unsubscribe;
  }, [hydrate, logout, onSignedOut]);

  return <>{children}</>;
};
